import { getErrorMessage } from '../../utils/errorMessage';
import NewCanvasDialog from '../canvas/NewCanvasDialog';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Clock3,
  LayoutGrid,
  Paperclip,
  PenLine,
  RefreshCw,
} from 'lucide-react';
import api from '../../utils/api';
import BorderGlow from '../components/BorderGlow';
import LiquidCreateButton from '../components/LiquidCreateButton.tsx';
import VisualLinkCard from '../components/VisualLinkCard';
import { getPublishedVideoCases } from '../learn/videoCaseApi';
import useHomeData from './useHomeData';
import AnimatedBrandText from './AnimatedBrandText';
import { useTeamBrand } from '../branding/TeamBrandContext';

const HOME_CASE_LIMIT = 4;

const CREATION_MODES = {
  writing: {
    label: '写剧本',
    description: '从想法开始，小影陪你把设定、大纲和正文一步步写清楚。',
    placeholder: '比如：一个替姐姐嫁进豪门的女孩，发现姐姐的死与这家人有关……',
    inputLabel: '描述你想写的故事',
    uploadLabel: '上传剧本审核',
    buttonLabel: '进入写作台',
    loadingLabel: '正在准备写作台',
    fallbackTitle: '未命名故事',
  },
  canvas: {
    label: '做成片',
    description: '已有剧本、画面想法或制作要求，直接交给画布 Agent 开始编排。',
    placeholder: '比如：把这段剧本做成 60 秒竖屏短剧，先拆分镜并准备人物和场景……',
    inputLabel: '描述你想制作的画面',
    uploadLabel: '进入画布后上传素材',
    buttonLabel: '进入画布',
    loadingLabel: '正在准备画布',
    fallbackTitle: '未命名画布',
  },
};

const PENDING_ENTRY_KEY = 'xiaoying:home:pending-entry';

function entryToken() {
  return `home-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function greetingWord() {
  const hour = new Date().getHours();
  if (hour < 5) return '夜深了';
  if (hour < 12) return '早上好';
  if (hour < 18) return '下午好';
  return '晚上好';
}

function timeAgo(value) {
  if (!value) return '最近编辑';
  const seconds = Math.max(0, (Date.now() - new Date(value).getTime()) / 1000);
  if (!Number.isFinite(seconds)) return '最近编辑';
  if (seconds < 60) return '刚刚';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟前`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} 小时前`;
  return `${Math.floor(seconds / 86400)} 天前`;
}

function RecentSkeleton() {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label="正在加载最近项目">
      {[0, 1, 2].map(item => (
        <div key={item} className="h-[116px] animate-pulse rounded-[20px] border border-black/[0.05] bg-white/55 dark:border-white/[0.06] dark:bg-white/[0.04]" />
      ))}
    </div>
  );
}

function EntryCardsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" aria-label="正在加载精选案例">
      {Array.from({ length: HOME_CASE_LIMIT }, (_, index) => (
        <div
          key={index}
          className="min-h-[218px] animate-pulse rounded-[24px] border border-black/[0.05] bg-white/55 dark:border-white/[0.06] dark:bg-white/[0.04] xl:aspect-[4/3] xl:min-h-0"
        />
      ))}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const brand = useTeamBrand();
  const outletContext = useOutletContext() || {};
  const { user, userLoading, preview = false } = outletContext;
  const authenticated = preview || Boolean(user) || Boolean(localStorage.getItem('token'));
  const personalizedGreeting = preview || Boolean(user);
  const { loading, recent, failedSources, retry } = useHomeData({ preview, authenticated });
  const [entryCards, setEntryCards] = useState([]);
  const [entryLoading, setEntryLoading] = useState(true);
  const [entryError, setEntryError] = useState('');
  const [creationMode, setCreationMode] = useState('writing');
  const [idea, setIdea] = useState('');
  const [creating, setCreating] = useState(false);
  const [canvasEntry, setCanvasEntry] = useState(null);
  const [createError, setCreateError] = useState('');
  const inputRef = useRef(null);
  const resumedEntryRef = useRef(false);
  const activeMode = CREATION_MODES[creationMode];

  const loadEntryCards = useCallback(async () => {
    setEntryLoading(true);
    setEntryError('');
    try {
      const cases = await getPublishedVideoCases();
      const orderedCases = [...cases].sort((left, right) => {
        const sortDifference = Number(right.sort || 0) - Number(left.sort || 0);
        if (sortDifference !== 0) return sortDifference;
        return Number(right.id || 0) - Number(left.id || 0);
      });
      setEntryCards(orderedCases.slice(0, HOME_CASE_LIMIT));
    } catch (error) {
      setEntryCards([]);
      setEntryError(getErrorMessage(error, '精选案例加载失败'));
    } finally {
      setEntryLoading(false);
    }
  }, []);

  useEffect(() => { loadEntryCards(); }, [loadEntryCards]);

  const greetingName = useMemo(() => {
    if (userLoading) return '创作者';
    return user?.username || '创作者';
  }, [user, userLoading]);

  const createEntry = useCallback(async (mode, rawText) => {
    const text = String(rawText || '').trim();
    const config = CREATION_MODES[mode] || CREATION_MODES.writing;
    setCreateError('');

    if (!authenticated) {
      try {
        sessionStorage.setItem(PENDING_ENTRY_KEY, JSON.stringify({ mode, text }));
      } catch { /* 浏览器不允许写 sessionStorage 时仍可正常去登录 */ }
      navigate('/login', { state: { from: '/home?resume=creation' } });
      return;
    }
    if (creating) return;

    setCreating(true);
    try {
      const title = text ? (text.length > 18 ? `${text.slice(0, 18)}…` : text) : config.fallbackTitle;
      const token = entryToken();
      const query = text ? `?send=${encodeURIComponent(text)}&entry=${encodeURIComponent(token)}` : '';

      if (preview) {
        navigate(mode === 'canvas'
          ? `/prototype/vnext/canvas/preview-canvas${query}`
          : `/prototype/vnext/writing/preview-new${query}`);
        return;
      }

      if (mode === 'canvas') {
        setCanvasEntry({ title: text ? title : '', query });
      } else {
        const response = await api.post('/agent/projects', { title });
        navigate(`/writing/${response.data.id}${query}`);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          sessionStorage.setItem(PENDING_ENTRY_KEY, JSON.stringify({ mode, text }));
        } catch { /* 同上 */ }
        navigate('/login', { state: { from: '/home?resume=creation' } });
        return;
      }
      setCreateError(getErrorMessage(`${config.label}项目暂时没有创建成功，想法还在，可以重试。`));
    } finally {
      setCreating(false);
    }
  }, [authenticated, creating, navigate, preview]);

  const start = () => createEntry(creationMode, idea);

  useEffect(() => {
    if (preview || !authenticated || resumedEntryRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('resume') !== 'creation') return;
    resumedEntryRef.current = true;
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(PENDING_ENTRY_KEY) || 'null');
      sessionStorage.removeItem(PENDING_ENTRY_KEY);
    } catch { /* 没有可恢复的入口就留在首页 */ }
    if (!pending?.mode) return;
    setCreationMode(pending.mode);
    setIdea(pending.text || '');
    createEntry(pending.mode, pending.text || '');
  }, [authenticated, createEntry, preview]);

  const openEntry = href => {
    if (preview && (href.startsWith('/learn') || href.startsWith('/writing'))) {
      navigate(`/prototype/vnext${href}`);
      return;
    }
    if (!authenticated && !href.startsWith('/learn')) {
      navigate('/login', { state: { from: href } });
      return;
    }
    navigate(href);
  };

  return (
    <div className="relative min-h-full overflow-hidden bg-[var(--vnext-page)]">
      {canvasEntry && <NewCanvasDialog initialName={canvasEntry.title} onClose={() => setCanvasEntry(null)} onCreated={canvas => navigate(`/canvas/${canvas.id}${canvasEntry.query}`)} />}
      <div className="pointer-events-none absolute inset-0 hidden sm:block" aria-hidden="true">
        <div className="absolute -left-20 -top-40 h-[460px] w-[460px] rounded-full bg-[#cbbce8]/25 blur-[130px] dark:bg-[#7a6a9e]/12" />
        <div className="absolute right-[-150px] top-[8%] h-[520px] w-[520px] rounded-full bg-[#cbd8cf]/30 blur-[150px] dark:bg-[#668278]/10" />
      </div>

      <div className="relative mx-auto max-w-[1240px] px-5 pb-16 pt-6 sm:px-8 lg:px-12">
        <section className="mx-auto max-w-[790px] pb-5 pt-[58px] text-center md:pt-[56px]">
          <div className="mb-5 inline-flex items-center gap-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400 dark:text-white/34">
            <span className="h-px w-5 bg-black/[0.16] dark:bg-white/[0.18]" aria-hidden="true" />
            从一个想法开始
            <span className="h-px w-5 bg-black/[0.16] dark:bg-white/[0.18]" aria-hidden="true" />
          </div>
          <h1 className="text-[34px] font-semibold leading-[1.1] tracking-[-0.045em] text-[#26222a] dark:text-[#f4f0f7] md:text-[48px]">
            {personalizedGreeting ? (
              <>{greetingWord()}，{greetingName}</>
            ) : (
              <>把灵感交给 <AnimatedBrandText /></>
            )}
          </h1>
          <p className="mt-4 text-[14px] leading-6 text-stone-500 dark:text-white/46">
            {brand.text(activeMode.description)}
          </p>

          <div className="mt-5 inline-flex rounded-[17px] border border-black/[0.07] bg-white/55 p-1 shadow-[0_18px_45px_-34px_rgba(52,43,65,.8)] backdrop-blur dark:border-white/[0.08] dark:bg-white/[0.045]" role="tablist" aria-label="选择创作方式">
            {Object.entries(CREATION_MODES).map(([mode, config]) => {
              const Icon = mode === 'writing' ? PenLine : LayoutGrid;
              const selected = creationMode === mode;
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`inline-flex min-w-[128px] items-center justify-center gap-2 rounded-[13px] px-5 py-2.5 text-[13px] font-semibold transition ${selected
                    ? 'bg-[#29252d] text-white shadow-[0_12px_28px_-16px_rgba(27,22,32,.9)] dark:bg-[#eee9f2] dark:text-[#27232b]'
                    : 'text-stone-500 hover:bg-white/70 hover:text-stone-800 dark:text-white/38 dark:hover:bg-white/[0.06] dark:hover:text-white/72'}`}
                  onClick={() => {
                    setCreationMode(mode);
                    setCreateError('');
                    requestAnimationFrame(() => inputRef.current?.focus());
                  }}
                  key={mode}
                >
                  <Icon size={15} />
                  {config.label}
                </button>
              );
            })}
          </div>

          <BorderGlow
            edgeSensitivity={30}
            glowColor="266 42 66"
            backgroundColor="var(--vnext-composer)"
            borderRadius={28}
            glowRadius={40}
            glowIntensity={1}
            coneSpread={25}
            animated={false}
            colors={['#9784c2', '#b88eb9', '#7f99c5']}
            fillOpacity={0.2}
            className="home-composer-glow mt-4 w-full"
          >
            <div className="relative w-full bg-transparent p-3 text-left">
              <textarea
                ref={inputRef}
                value={idea}
                onChange={event => setIdea(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    start();
                  }
                }}
                rows={4}
                placeholder={activeMode.placeholder}
                className="min-h-[112px] w-full resize-none bg-transparent px-3 py-2 text-[15px] leading-7 text-stone-800 outline-none placeholder:text-stone-400 dark:text-white/88 dark:placeholder:text-white/28"
                aria-label={activeMode.inputLabel}
              />
              <div className="flex items-center justify-between gap-4 border-t border-black/[0.06] px-1 pt-3 dark:border-white/[0.07]">
                <button
                  type="button"
                  onClick={() => creationMode === 'canvas'
                    ? createEntry('canvas', '')
                    : openEntry('/writing?tab=review&mode=upload')}
                  className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[12px] font-medium text-stone-500 transition hover:bg-stone-100 hover:text-stone-800 dark:text-white/40 dark:hover:bg-white/[0.06] dark:hover:text-white/75"
                >
                  <Paperclip size={15} />
                  {activeMode.uploadLabel}
                </button>
                <LiquidCreateButton
                  onClick={start}
                  disabled={creating}
                  loading={creating}
                  label={activeMode.buttonLabel}
                  loadingLabel={activeMode.loadingLabel}
                />
              </div>
            </div>
          </BorderGlow>

          {createError && (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-[12px] text-rose-600 dark:text-rose-300" role="alert">
              <span>{createError}</span>
              <button type="button" onClick={start} className="font-semibold underline underline-offset-2">重试</button>
            </div>
          )}
          <p className="mt-3 text-[10px] text-stone-400 dark:text-white/24">Enter 发送，Shift + Enter 换行</p>
        </section>

        <section aria-labelledby="entry-heading">
          <div className="mb-2 flex items-end justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400 dark:text-white/28">Create your way</p>
              <h2 id="entry-heading" className="mt-1 text-[20px] font-semibold tracking-[-0.03em]">从这里开始</h2>
            </div>
          </div>
          {entryLoading ? (
            <EntryCardsSkeleton />
          ) : entryError ? (
            <div className="grid min-h-[218px] place-items-center rounded-[24px] border border-dashed border-black/[0.1] bg-white/30 px-6 text-center dark:border-white/[0.09] dark:bg-white/[0.02]" role="alert">
              <div>
                <p className="text-[13px] font-medium">精选案例暂时没有加载成功</p>
                <p className="mt-1.5 text-[11px] text-stone-400 dark:text-white/34">{entryError}</p>
                <button type="button" onClick={loadEntryCards} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]">
                  <RefreshCw size={12} />重新加载
                </button>
              </div>
            </div>
          ) : entryCards.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {entryCards.map(card => (
                <VisualLinkCard
                  key={card.id}
                  image={card.cover || card.image_url || ''}
                  eyebrow={card.tag || card.category_name || card.category || '精选案例'}
                  title={card.title}
                  description={card.sub_title || card.creation_tags || '查看完整案例与创作方法。'}
                  onClick={() => navigate(preview ? '/prototype/vnext/learn' : `/learn/${encodeURIComponent(card.id)}`)}
                  className="xl:aspect-[4/3] xl:min-h-0"
                />
              ))}
            </div>
          ) : (
            <div className="grid min-h-[218px] place-items-center rounded-[24px] border border-dashed border-black/[0.1] bg-white/30 px-6 text-center dark:border-white/[0.09] dark:bg-white/[0.02]">
              <div>
                <BookOpen size={21} className="mx-auto mb-3 text-[#8c7db0]" />
                <p className="text-[13px] font-medium">暂无已上架的精选案例</p>
                <button type="button" onClick={() => navigate(preview ? '/prototype/vnext/learn' : '/learn')} className="mt-2 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]">前往学习与案例</button>
              </div>
            </div>
          )}
        </section>

        <section className="mt-12" aria-labelledby="recent-heading">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400 dark:text-white/28">Continue creating</p>
              <h2 id="recent-heading" className="mt-1 text-[20px] font-semibold tracking-[-0.03em]">最近项目</h2>
            </div>
            {failedSources.length > 0 && (
              <button
                type="button"
                onClick={retry}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-stone-500 transition hover:text-stone-900 dark:text-white/38 dark:hover:text-white"
              >
                <RefreshCw size={12} />
                {failedSources.join('、')}暂未加载，重试
              </button>
            )}
          </div>

          {!authenticated ? (
            <div className="flex min-h-[144px] flex-col items-center justify-center rounded-[22px] border border-dashed border-black/[0.1] bg-white/30 px-6 text-center dark:border-white/[0.09] dark:bg-white/[0.02]">
              <BookOpen size={21} className="mb-3 text-[#8c7db0]" />
              <p className="text-[13px] font-medium">登录后查看并继续最近项目</p>
              <button
                type="button"
                onClick={() => navigate('/login', { state: { from: '/home' } })}
                className="mt-2 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]"
              >
                {brand.text('登录小影')}
              </button>
            </div>
          ) : loading ? (
            <RecentSkeleton />
          ) : recent.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {recent.map((item, index) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => navigate(item.href)}
                  className="group flex min-h-[116px] items-center gap-4 rounded-[20px] border border-black/[0.06] bg-white/58 p-4 text-left shadow-[0_18px_44px_-38px_rgba(54,45,36,.65)] backdrop-blur transition hover:-translate-y-0.5 hover:border-[#8c7db0]/25 hover:bg-white dark:border-white/[0.07] dark:bg-white/[0.035] dark:hover:border-[#b9a7e7]/25 dark:hover:bg-white/[0.055]"
                >
                  <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] ${
                    index % 3 === 0
                      ? 'bg-[#e8e1ef] text-[#74628f] dark:bg-[#766395]/25 dark:text-[#c8b8ea]'
                      : index % 3 === 1
                        ? 'bg-[#dfe8e2] text-[#667b6e] dark:bg-[#688074]/20 dark:text-[#a9c3b2]'
                        : 'bg-[#ebe5db] text-[#7c6e5d] dark:bg-[#84725d]/20 dark:text-[#c8b79f]'
                  }`}>
                    {item.type === 'writing' ? <PenLine size={21} /> : <LayoutGrid size={21} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-semibold text-stone-800 dark:text-white/86">{item.title}</span>
                    <span className="mt-1.5 flex items-center gap-2 text-[11px] text-stone-400 dark:text-white/34">
                      <span>{item.type === 'writing' ? '剧本' : '画布'}</span>
                      <span>·</span>
                      <span>{item.stage}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><Clock3 size={11} />{timeAgo(item.time)}</span>
                    </span>
                  </span>
                  <ArrowRight size={15} className="text-stone-300 transition group-hover:translate-x-0.5 group-hover:text-[#7a679c] dark:text-white/18 dark:group-hover:text-[#c8b8ea]" />
                </button>
              ))}
            </div>
          ) : (
            <div className="flex min-h-[144px] flex-col items-center justify-center rounded-[22px] border border-dashed border-black/[0.1] bg-white/30 px-6 text-center dark:border-white/[0.09] dark:bg-white/[0.02]">
              {failedSources.length === 2 ? (
                <>
                  <RefreshCw size={20} className="mb-3 text-stone-400" />
                  <p className="text-[13px] font-medium">项目列表暂时没有加载成功</p>
                  <button type="button" onClick={retry} className="mt-2 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]">重新加载</button>
                </>
              ) : (
                <>
                  <BookOpen size={21} className="mb-3 text-[#8c7db0]" />
                  <p className="text-[13px] font-medium">还没有项目，从上面的一个想法开始吧</p>
                  <button type="button" onClick={() => inputRef.current?.focus()} className="mt-2 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]">写下第一个故事</button>
                </>
              )}
            </div>
          )}
        </section>

        <footer className="mt-14 border-t border-black/[0.06] py-6 text-[10px] text-stone-400 dark:border-white/[0.06] dark:text-white/24">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{brand.text('小影 · 为短剧创作者而生')}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span>{brand.active ? brand.name : 'xiaoying.studio'}</span>
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              className="transition hover:text-stone-600 dark:hover:text-white/50"
            >
              苏ICP备2026053046号-1
            </a>
            {/* 公安备案上线后替换此占位 */}
            <span className="opacity-0 select-none" aria-hidden="true">公安备案</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
